/**
 * Режим «Карта» беты (ISSUE-164): бой атаки участка и журнал атаки.
 *
 * Бой атаки — тот же бой движка (`createBattle`), но «зеркальный»: в центре база-цель с прочностью, из неё выходят
 * защитники (зомби волнами или копии героев владельца под ИИ), герои атакующего стоят по краю на платформах с
 * прочностью. Победа — база разрушена до конца таймера; поражение — время вышло или пали все платформы с героями.
 * Состав защиты фиксирует сервер при старте атаки ({@link AttackDefense}) вместе с сидом и состоянием забежки атакующего.
 */
import { z } from "zod";
import { BalanceVersion } from "./balance";
import { TimedCommand } from "./commands";
import { ContentId, PosInt, Seed, UInt } from "./common";
import { RunHero, RunState, WaveResult } from "./wave-log";

/** Защита свободного участка: зомби волнами выходят из базы. */
export const AttackDefenseZombies = z.object({
  kind: z.literal("zombies"),
  /** Прочность базы. */
  baseHp: PosInt,
  /** Волна, по которой подбираются виды зомби (`minWave`). */
  zombieWave: PosInt,
  /** Рост прочности зомби и щитов, промилле (1000 — как в данных). */
  hpPermille: PosInt,
  /** Зомби в волне защитников, пауза между волнами и до первой, тиков. */
  waveSize: PosInt,
  waveGapTicks: PosInt,
  firstWaveTicks: UInt,
  /** Босс в середине боя. */
  bossId: ContentId.nullable(),
});
export type AttackDefenseZombies = z.infer<typeof AttackDefenseZombies>;

/** Защита чужого участка: копии героев владельца (расстановка, уровни, звёзды, Tech на момент старта атаки). */
export const AttackDefenseHeroes = z.object({
  kind: z.literal("heroes"),
  baseHp: PosInt,
  heroes: z.array(RunHero).max(16),
  tech: z.record(ContentId, UInt),
});
export type AttackDefenseHeroes = z.infer<typeof AttackDefenseHeroes>;

export const AttackDefense = z.discriminatedUnion("kind", [AttackDefenseZombies, AttackDefenseHeroes]);
export type AttackDefense = z.infer<typeof AttackDefense>;

/** Версия формата журнала атаки. */
export const ATTACK_LOG_FORMAT = 1;

/**
 * Журнал атаки — то, что клиент шлёт серверу после боя. Сервер берёт `start`, `seed` и `defense` из записи атаки
 * (а не из журнала), прогоняет engine и сам решает исход; `result` клиента нужен только для сверки.
 * `result.hqHp` — остаток прочности базы, `result.coins` — всегда 0.
 */
export const AttackLog = z.object({
  format: z.literal(ATTACK_LOG_FORMAT),
  attackId: z.uuid(),
  seed: Seed,
  balanceVersion: BalanceVersion,
  clientVersion: z.string().min(1).max(64),
  start: RunState,
  defense: AttackDefense,
  commands: z.array(TimedCommand).max(2000),
  result: WaveResult,
});
export type AttackLog = z.infer<typeof AttackLog>;
